import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import SearchableListPicker, { type SearchableOption } from '@core/components/SearchableListPicker';
import { fetchAllChurchesForDistrict } from '@features/church/api/churchApi';
import type { Church } from '@features/church/types/church';
import {
  updateDistrictHeadChurch,
  updateNationDistrictHeadChurch,
} from '@features/district/api/districtApi';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import type { ErrorResponse } from '@core/types/errorResponse';

interface Props {
  show: boolean;
  districtId: number;
  districtName: string;
  /** When set, saves via nation/.../head-church/update; otherwise district scope. */
  nationId?: number | null;
  currentHeadChurchId?: number | null;
  onHide: () => void;
  onSaved: () => void;
}

const NONE_VALUE = '';

const SetHeadChurchModal: React.FC<Props> = ({
  show,
  districtId,
  districtName,
  nationId,
  currentHeadChurchId = null,
  onHide,
  onSaved,
}) => {
  const [churches, setChurches] = useState<Church[]>([]);
  const [loadingChurches, setLoadingChurches] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number>(NONE_VALUE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !districtId) return;
    let cancelled = false;
    setLoadingChurches(true);
    setError(null);
    setSelectedId(currentHeadChurchId != null ? currentHeadChurchId : NONE_VALUE);
    fetchAllChurchesForDistrict(districtId)
      .then((response) => {
        if (cancelled) return;
        if (!response.isSuccess()) {
          setError((response.getError() as ErrorResponse | undefined)?.detail ?? 'Failed to load churches.');
          setChurches([]);
          return;
        }
        setChurches(response.getData()?.data ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoadingChurches(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, districtId, currentHeadChurchId]);

  const options: SearchableOption[] = useMemo(() => {
    const churchOptions = [...churches]
      .sort((a, b) => a.church_name.localeCompare(b.church_name, undefined, { sensitivity: 'base' }))
      .map((church) => ({
        value: church.church_id,
        label: church.church_name,
        ...(church.is_active === false ? { sublabel: '(Inactive)' } : {}),
      }));
    return [{ value: NONE_VALUE, label: 'None' }, ...churchOptions];
  }, [churches]);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const headChurchId =
      selectedId === NONE_VALUE || selectedId === '' || selectedId == null
        ? null
        : Number(selectedId);
    const response =
      nationId != null && nationId > 0
        ? await updateNationDistrictHeadChurch(nationId, districtId, headChurchId)
        : await updateDistrictHeadChurch(districtId, headChurchId);
    setBusy(false);
    if (!response.isSuccess()) {
      setError((response.getError() as ErrorResponse | undefined)?.detail ?? 'Failed to update head church.');
      return;
    }
    showSuccess(response.getData()?.message ?? 'District head church updated successfully');
    onSaved();
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="text-break">
          Set head church for district {districtName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        {loadingChurches ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading churches…
          </div>
        ) : (
          <Form.Group>
            <Form.Label>Head Church</Form.Label>
            <SearchableListPicker
              options={options}
              value={selectedId}
              onChange={setSelectedId}
              placeholder="None"
              searchPlaceholder="Search churches…"
              disabled={busy}
              italicSublabel
              testId="set-head-church-picker"
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={busy || loadingChurches}>
          {busy ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SetHeadChurchModal;
